import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, LayoutList, LayoutGrid, AlignJustify, Zap, Settings2 } from 'lucide-react';
import { useProject, useDeleteProject } from '../hooks/useProjects';
import { useTickets, useTicketStats } from '../hooks/useTickets';
import {
  useProjectRisk,
  useRiskHistory,
  useRefreshRisk,
} from '../hooks/useInsights';
import { useUserStore } from '../store/userStore';
import { RiskEvaluation } from '../components/RiskEvaluation';
import { ProjectProgressBar } from '../components/ProjectProgressBar';
import { ProjectActivityFeed } from '../components/ProjectActivityFeed';
import { ProjectLinks } from '../components/ProjectLinks';
import { ProjectGoals } from '../components/ProjectGoals';
import { VelocityPanel } from '../components/VelocityPanel';
import { TicketCard } from '../components/TicketCard';
import { TicketDetailPanel } from '../components/TicketDetailPanel';
import { ProjectStatusChip } from '../components/StatusChip';
import { AdaptiveView } from '../components/project/AdaptiveView';
import { ProjectSettingsPanel } from '../components/project/ProjectSettingsPanel';
import type { Ticket, TicketStatus } from '../types';
import { track } from '../lib/telemetry';
import {
  type TrendDirection,
  riskTrendToDirection,
  directionFromSeries,
  trendSentiment,
} from '../lib/trend';
import { TrendIndicator } from '../components/TrendIndicator';

// ── Constants ─────────────────────────────────────────────────────────────────

const TICKET_STATUS_ORDER: TicketStatus[] = ['triage', 'at_risk', 'on_track', 'planning', 'complete', 'idea', 'invalid'];

const TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
  triage:   'Triage',
  at_risk:  'At Risk',
  on_track: 'On Track',
  planning: 'Planning',
  complete: 'Complete',
  idea:     'Ideas',
  invalid:  'Invalid / Won\'t Do',
};

const TICKET_STATUS_COLORS: Record<TicketStatus, string> = {
  triage:   'text-orange-600 dark:text-orange-400',
  at_risk:  'text-red-600 dark:text-red-400',
  on_track: 'text-emerald-600 dark:text-emerald-400',
  planning: 'text-blue-600 dark:text-blue-400',
  complete: 'text-gray-500 dark:text-gray-400',
  idea:     'text-purple-600 dark:text-purple-400',
  invalid:  'text-gray-400 dark:text-gray-500',
};

// ── RiskHistoryMini ───────────────────────────────────────────────────────────

function RiskHistoryMini({ projectId, riskTrend }: { projectId: string; riskTrend?: string }) {
  const { data: history } = useRiskHistory(projectId, 30);

  if (!history || history.length < 2) return null;

  const maxScore = 5;
  const width = 240;
  const height = 48;
  const points = history.map((h, i) => {
    const x = (i / (history.length - 1)) * width;
    const y = height - (h.score / maxScore) * height;
    return `${x},${y}`;
  });

  const first = history[0];
  const latest = history[history.length - 1];

  // One trend per surface: the canonical backend riskTrend when known,
  // otherwise the drawn series' own shape (shared ±0.3 cut, unknown below
  // 3 points — see lib/trend.ts). 'unknown' stays unknown; it must never
  // collapse into "Stable".
  const canonical = riskTrendToDirection(riskTrend);
  const direction: TrendDirection =
    canonical !== 'unknown' ? canonical : directionFromSeries(history.map((h) => h.score));

  const currentStale = latest.staleCount ?? 0;
  const staleDelta =
    latest.staleCount != null && first.staleCount != null
      ? latest.staleCount - first.staleCount
      : null;

  // Stroke follows the same resolved direction as the label beside it —
  // risk is lower-is-better, so 'down' draws emerald and 'up' draws red.
  const sentiment = trendSentiment(direction, 'lower-is-better');
  const strokeColor =
    sentiment === 'positive'
      ? 'rgba(16,185,129,0.7)'
      : sentiment === 'negative'
        ? 'rgba(248,113,113,0.7)'
        : 'rgba(57,148,255,0.6)';

  return (
    <div className="mt-4 p-3 bg-gray-50 dark:bg-white/[0.03] rounded-lg border border-gray-200 dark:border-white/5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-meta font-semibold text-gray-600 dark:text-gray-300">30-Day Risk Trend</span>
        <div className="flex items-center gap-2">
          <span className={`text-meta ${currentStale > 0 ? 'text-red-400' : 'text-gray-400'}`}>
            {currentStale} stale
            {staleDelta != null && staleDelta !== 0 && history.length >= 3 && (
              <span className={staleDelta < 0 ? 'text-emerald-500' : 'text-red-400'}>
                {' '}({staleDelta > 0 ? '+' : ''}{staleDelta})
              </span>
            )}
          </span>
          {direction === 'unknown' ? (
            // Chart exists but is too young to claim a direction — honest
            // copy for why there is no trend yet, still neutral.
            <span className="text-meta font-medium text-gray-400">· Building trend</span>
          ) : (
            <TrendIndicator direction={direction} polarity="lower-is-better" />
          )}
        </div>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" preserveAspectRatio="none" style={{ height: '48px' }}>
        <polyline points={points.join(' ')} fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <div className="flex justify-between mt-1.5">
        <span className="text-meta text-gray-400">{first.date}</span>
        <span className="text-meta text-gray-400">{latest.date}</span>
      </div>
    </div>
  );
}

// ── Delete confirm modal ──────────────────────────────────────────────────────

function DeleteConfirmModal({
  projectName, onConfirm, onCancel, isDeleting,
}: { projectName: string; onConfirm: () => void; onCancel: () => void; isDeleting: boolean }) {
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-white/10 p-6 w-full max-w-sm shadow-xl">
        <h3 className="text-title text-gray-900 dark:text-white mb-2">Delete Project</h3>
        <p className="text-meta text-gray-500 mb-5">
          Are you sure you want to delete <strong>{projectName}</strong>? This cannot be undone.
        </p>
        <div className="flex gap-3 justify-end">
          <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-gray-200 dark:border-white/10 text-meta text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="px-4 py-2 rounded-lg bg-red-600 text-white text-meta font-medium hover:bg-red-700 disabled:opacity-50"
          >
            {isDeleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── View tab type ─────────────────────────────────────────────────────────────

type PanelView = 'adaptive' | 'detail' | 'settings';
type TicketDensity = 'list' | 'card' | 'large';

// ── Main component ────────────────────────────────────────────────────────────

export function ProjectDashboard() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { sessionConfig, activeRole, currentUser } = useUserStore();
  const effectiveRole: string =
    activeRole ??
    currentUser?.primaryRole ??
    sessionConfig?.functionalRoles?.[0] ??
    sessionConfig?.role ??
    'viewer';

  const [panelView, setPanelView] = useState<PanelView>('adaptive');
  const panelViewRef = useRef<PanelView>('adaptive');
  const viewStartRef = useRef<number>(Date.now());
  const activeViewRef = useRef<string>('adaptive');
  const [ticketDensity, setTicketDensity] = useState<TicketDensity>(
    () => (localStorage.getItem('octopm.ticketViewMode') as TicketDensity) ?? 'list'
  );
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  function handleSetDensity(mode: TicketDensity) {
    setTicketDensity(mode);
    localStorage.setItem('octopm.ticketViewMode', mode);
    track('project.ticket_density_changed', 'action', {
      projectId: id ?? '',
      mode,
      role: effectiveRole ?? '',
    });
  }

  const effectiveRoleRef = useRef(effectiveRole);
  effectiveRoleRef.current = effectiveRole;
  const projectIdRef = useRef(id);
  projectIdRef.current = id;

  useEffect(() => {
    track('project.opened', 'feature_use', { projectId: projectIdRef.current!, role: effectiveRoleRef.current });
    viewStartRef.current = Date.now();
    return () => {
      track('project.view.time_spent', 'feature_use', {
        projectId: projectIdRef.current!,
        view: activeViewRef.current,
        role: effectiveRoleRef.current,
        durationMs: Date.now() - viewStartRef.current,
      });
    };
  }, []);

  const { data: project, isLoading: projectLoading } = useProject(id);
  const { data: tickets, isLoading: ticketsLoading } = useTickets(id);
  const { data: ticketStats } = useTicketStats(id);
  const { data: risk, isLoading: riskLoading } = useProjectRisk(id);
  const { mutate: refreshRisk, isPending: refreshingRisk } = useRefreshRisk();
  const { mutate: deleteProject, isPending: deleting } = useDeleteProject();

  const canRefresh =
    sessionConfig?.entityRole === 'admin' ||
    sessionConfig?.role === 'admin' ||
    sessionConfig?.functionalRoles?.includes('product_manager') ||
    sessionConfig?.role === 'product_manager' ||
    sessionConfig?.functionalRoles?.includes('executive') ||
    sessionConfig?.role === 'executive';

  const canDelete =
    !project?.isParkingLot && (
      sessionConfig?.entityRole === 'admin' ||
      sessionConfig?.role === 'admin' ||
      sessionConfig?.functionalRoles?.includes('product_manager') ||
      sessionConfig?.role === 'product_manager'
    );

  const ticketsByStatus = TICKET_STATUS_ORDER.reduce<Record<TicketStatus, Ticket[]>>(
    (acc, status) => {
      acc[status] = (tickets ?? []).filter((t) => t.status === status);
      return acc;
    },
    { triage: [], at_risk: [], on_track: [], planning: [], complete: [], idea: [], invalid: [] }
  );

  function handleDelete() {
    if (!id) return;
    deleteProject(id, {
      onSuccess: () => {
        track('project.deleted', 'action', { projectId: id });
        navigate('/');
      },
    });
  }

  if (projectLoading) {
    return (
      <div className="px-6 sm:px-10 lg:px-16 pt-6 space-y-4">
        <div className="skeleton h-8 w-64 rounded" />
        <div className="skeleton h-5 w-32 rounded" />
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-6">
          <div className="lg:col-span-2 skeleton h-96 rounded-xl" />
          <div className="lg:col-span-3 skeleton h-96 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="px-6 sm:px-10 lg:px-16 pt-6 text-center text-gray-500">
        <p className="text-body font-medium">Project not found.</p>
        <Link to="/" className="text-primary text-meta mt-2 inline-block">Back to dashboard</Link>
      </div>
    );
  }

  // ── View tabs ───────────────────────────────────────────────────────────────

  const tabs: { view: PanelView; label: string; icon: React.ElementType }[] = [
    { view: 'adaptive', label: 'Overview',   icon: Zap },
    { view: 'detail',   label: 'Detail',     icon: LayoutList },
    { view: 'settings', label: 'Project Settings', icon: Settings2 },
  ];

  return (
    <div>
      {showDeleteConfirm && (
        <DeleteConfirmModal
          projectName={project.name}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
          isDeleting={deleting}
        />
      )}

      {/* ── Sticky header ────────────────────────────────────────────────── */}
      <div
        data-demo-id="project-dashboard-header"
        className="sticky top-0 z-10 backdrop-blur-md bg-[#f9fafb]/90 dark:bg-bg-deep/90 pb-3"
        style={{ maskImage: 'linear-gradient(to bottom, black calc(100% - 16px), transparent 100%)' }}
      >
        <div className="flex items-start justify-between gap-4 px-6 sm:px-10 lg:px-16 pt-6 pb-4">
          <div>
            {/*
              Both labels point at the portfolio view (RoleDashboard's
              viewMode=portfolio) — that's where both the regular project
              list AND the Parking Lot section actually live (see
              PMDashboard). Linking to "/" here used to reset viewMode to
              'today' and strand the user on the Today briefing instead of
              back where they came from.
            */}
            <Link
              to="/?view=portfolio"
              className="flex items-center gap-1.5 text-meta text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors mb-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {project.isParkingLot ? 'Parking Lot' : 'Portfolio'}
            </Link>
            <h1 className="text-headline text-gray-900 dark:text-white font-display">{project.name}</h1>
            <div className="flex items-center gap-3 mt-1.5">
              <ProjectStatusChip status={project.status} />
              {project.ownerName && (
                <span className="text-meta text-gray-500">Owner: {project.ownerName}</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Density toggle — only visible in Detail view */}
            {panelView === 'detail' && (
              <div className="flex items-center gap-0.5 bg-gray-100 dark:bg-white/5 rounded-lg p-0.5 border border-gray-200 dark:border-white/10">
                {([
                  { mode: 'list'  as TicketDensity, icon: LayoutList,   label: 'List' },
                  { mode: 'card'  as TicketDensity, icon: LayoutGrid,   label: 'Cards' },
                  { mode: 'large' as TicketDensity, icon: AlignJustify, label: 'Detail' },
                ] as const).map(({ mode, icon: Icon, label }) => (
                  <button
                    key={mode}
                    onClick={() => handleSetDensity(mode)}
                    title={label}
                    aria-label={label}
                    className={`p-1.5 rounded-md focus-visible:ring-2 focus-visible:ring-primary/50 transition-colors ${
                      ticketDensity === mode
                        ? 'bg-white dark:bg-white/15 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                  </button>
                ))}
              </div>
            )}

            {/* View switcher tabs */}
            <div className="flex items-center gap-0.5 bg-gray-100 dark:bg-white/5 rounded-lg p-0.5 border border-gray-200 dark:border-white/10">
              {tabs.map(({ view, label, icon: Icon }) => (
                <button
                  key={view}
                  onClick={() => {
                    if (view !== panelViewRef.current) {
                      const duration = Date.now() - viewStartRef.current;
                      track('project.view.time_spent', 'feature_use', {
                        projectId: project.projectId,
                        view: activeViewRef.current,
                        role: effectiveRole,
                        durationMs: duration,
                      });
                      track('project.view_switched', 'feature_use', {
                        projectId: project.projectId,
                        fromView: panelViewRef.current,
                        toView: view,
                      });
                      viewStartRef.current = Date.now();
                      activeViewRef.current = view;
                      panelViewRef.current = view;
                    }
                    setPanelView(view);
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-meta font-medium focus-visible:ring-2 focus-visible:ring-primary/50 transition-colors ${
                    panelView === view
                      ? 'bg-white dark:bg-white/15 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Progress bar strip */}
        {ticketStats && ticketStats.total > 0 && !project.isParkingLot && (
          <div className="px-6 sm:px-10 lg:px-16 pb-4">
            <ProjectProgressBar stats={ticketStats} height="md" />
          </div>
        )}
      </div>

      {/* ── Adaptive View ─────────────────────────────────────────────────── */}
      {panelView === 'adaptive' && (
        <AdaptiveView
          project={project}
          tickets={tickets ?? []}
          risk={risk ?? undefined}
          ticketStats={ticketStats}
          sessionConfig={sessionConfig}
          onTicketSelect={setSelectedTicket}
        />
      )}

      {/* ── Detail View ───────────────────────────────────────────────────── */}
      {panelView === 'detail' && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 px-6 sm:px-10 lg:px-16 pb-6 pt-4">
          {/* Left sidebar */}
          {!project.isParkingLot && (
            <div className="lg:col-span-2 space-y-4">
              <RiskEvaluation
                risk={risk ?? undefined}
                isLoading={riskLoading}
                isRefreshing={refreshingRisk}
                canRefresh={canRefresh}
                compact={!canRefresh}
                onRefresh={() => id && refreshRisk(id)}
              />
              {id && <RiskHistoryMini projectId={id} riskTrend={risk?.trend} />}
              <ProjectActivityFeed tickets={tickets ?? []} risk={risk ?? undefined} />
              <ProjectLinks project={project} />
              <ProjectGoals
                projectId={project.projectId}
                linkedKrIds={project.linkedKrIds ?? []}
                canEdit={canDelete ?? false}
              />
            </div>
          )}

          {/* Right: Ticket feed */}
          <div className={project.isParkingLot ? 'lg:col-span-5 space-y-5' : 'lg:col-span-3 space-y-5'}>
            {!project.isParkingLot && id && <VelocityPanel projectId={id} />}

            {ticketsLoading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => <div key={i} className="skeleton h-24 rounded-lg" />)}
              </div>
            ) : (
              TICKET_STATUS_ORDER.map((status) => {
                const group = ticketsByStatus[status];
                if (group.length === 0) return null;
                return (
                  <div key={status}>
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className={`text-title ${TICKET_STATUS_COLORS[status]}`}>
                        {TICKET_STATUS_LABELS[status]}
                      </h3>
                      <span className="text-meta text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-white/5 px-2 py-0.5 rounded-full">
                        {group.length}
                      </span>
                    </div>
                    {ticketDensity === 'list' ? (
                      <div className="divide-y divide-gray-100 dark:divide-white/5 rounded-lg overflow-hidden border border-gray-200 dark:border-white/5">
                        {group.map((ticket) => (
                          <TicketCard key={ticket.ticketId} ticket={ticket} viewMode={ticketDensity} onClick={() => setSelectedTicket(ticket)} />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {group.map((ticket) => (
                          <TicketCard key={ticket.ticketId} ticket={ticket} viewMode={ticketDensity} onClick={() => setSelectedTicket(ticket)} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}

            {!ticketsLoading && (tickets ?? []).length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <p className="text-body font-medium">No tickets yet for this project.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Settings View ─────────────────────────────────────────────────── */}
      {panelView === 'settings' && (
        <ProjectSettingsPanel
          project={project}
          sessionConfig={sessionConfig}
          canRefresh={canRefresh ?? false}
          canDelete={canDelete ?? false}
          isRefreshing={refreshingRisk}
          onRefresh={() => id && refreshRisk(id)}
          onDeleteRequest={() => setShowDeleteConfirm(true)}
        />
      )}

      <TicketDetailPanel ticket={selectedTicket} onClose={() => setSelectedTicket(null)} role={effectiveRole} />
    </div>
  );
}
